import React from 'react'
import { useHistory } from 'react-router-dom'

import GridContainer from '../../components/Grid/GridContainer.js'
import GridItem from '../../components/Grid/GridItem.js'
import Card from '../../components/Card/Card.js'
import CardBody from '../../components/Card/CardBody.js'
import CardIcon from '../../components/Card/CardIcon.js'
import CardHeader from '../../components/Card/CardHeader.js'
import AtlasTree from '../../components/AtlasTree/AtlasTree.js'

// material-ui components
import LinearProgress from '@material-ui/core/LinearProgress'
import Button from '@material-ui/core/Button'
import Snackbar from '@material-ui/core/Snackbar'
import Alert from '@material-ui/lab/Alert'
import ButtonGroup from '@material-ui/core/ButtonGroup'

import CategoryIcon from '@material-ui/icons/Category'
import VisibilityIcon from '@material-ui/icons/Visibility'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import DeleteIcon from '@material-ui/icons/Delete'

// components
import SiteSelector from '../Common/SiteSelector'
import AddCategoryDialog from './AddCategoryDialog'
import DeleteCategoryDialog from './DeleteCategoryDialog'
import { dashboardResponse, getCategories, addCategory, deleteCategory } from '../../layouts/Aws.js'
import AtlasButtonBar from 'components/AtlasButtonBar/AtlasButtonBar.js'

export default function Categories () {
  const history = useHistory()

  const [siteList, setSiteList] = React.useState()
  const [selectedSite, setSelectedSite] = React.useState()
  const [selectedCategory, setSelectedCategory] = React.useState(null)
  const [loading, setLoading] = React.useState(false) // if true, display the loading progress bar
  const [siteLoaded, setSiteLoaded] = React.useState(false)
  const [categoryData, setCategoryData] = React.useState([])
  const [addCategoryButtonText, setAddCategoryButtonText] = React.useState('Add Root Category')
  const [deleteCategoryButtonEnabled, setDeleteCategoryButtonEnabled] = React.useState(false)
  const [viewCategoryProductsButtonEnabled, setViewCategoryProductsButtonEnabled] = React.useState(false)
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = React.useState(false)
  const [addCategoryParentNode, setAddCategoryParentNode] = React.useState('Root')
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = React.useState(false)
  const [deleteCategoryNode, setDeleteCategoryNode] = React.useState('')
  const [snackbarOpen, setSnackbarOpen] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState('')
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('error')

  // Setup SiteSelection component
  const onSiteSelected = async (site) => {
    setSelectedCategory(null)
    setSelectedSite(site)
    setLoading(true)
    setSiteLoaded(false)

    const response = await getCategories(site)

    if (response.code === 200) {
      setCategoryData(response.payload)
    } else {
      console.log('load categories response:', response)

      setSnackbarMessage('Unable to load categories')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }

    setLoading(false)
    setSiteLoaded(true)
  }

  React.useEffect(() => {
    dashboardResponse().then(res => {
      setSiteList(res.sites)
    })
  }, [])

  // Setup AtlasTree component
  const categoryTreeRef = React.useRef() // get a reference to the grid to access it's api if needed

  // a function to use the category id as the row id - this is supposed to mean that upon reloading the data, it will keep the nodes expanded, and the selected one intact etc
  const getRowId = React.useCallback((params) => {
    return params.data.id
  }, [])

  const defaultColDef = React.useMemo(() => {
    return {
      sortable: false,
      filter: false,
      resizable: true,
      flex: 1
    }
  }, [])

  const [columnDefs] = React.useState([{
    field: 'nodeListString',
    headerName: 'Node List'
  }])

  const getCategoryCellRenderer = () => {
    class CategoryCellRenderer {
      init (params) {
        this.value = params.value
        this.editable = params.data.editable

        var wrapper = document.createElement('div')
        wrapper.innerHTML = this.value + (this.editable ? '' : ' <span class="material-icons" style="font-size: 16px; vertical-align: text-bottom">lock</span>')

        this.html = wrapper
      }

      getGui () {
        return this.html
      }

      refresh (params) {
        return false
      }
    }

    return CategoryCellRenderer
  }

  const autoGroupColumnDef = {
    headerName: 'Categories',
    flex: 2,
    cellRendererParams: {
      suppressCount: true,
      checkbox: true,
      innerRenderer: getCategoryCellRenderer()
    }
  }

  // function that defines how to work out the category hierarchy - here we split on their ids that we have built into a hierarchy divided by '::'
  const getDataPath = React.useCallback((data) => {
    return data.nodeListString.split('::')
  }, [])

  function handleCategorySelected (event) {
    console.log('event', event)

    const selectedNodes = event.api.getSelectedNodes()

    if (selectedNodes.length > 0) {
      const selectedRow = selectedNodes[0] // the tree only allows single select so we can grab the first node if there is now

      console.log(selectedRow)

      setSelectedCategory(selectedRow.data)
    } else {
      setSelectedCategory(null)
    }
  }

  // we can set the button states by observing the selectedCategory value
  React.useEffect(() => {
    if (selectedCategory != null) {
      setAddCategoryButtonText('Add Sub Category')
      setAddCategoryParentNode(selectedCategory.nodeListString)
      setViewCategoryProductsButtonEnabled(true)

      if (selectedCategory.editable) {
        setDeleteCategoryButtonEnabled(true)
        setDeleteCategoryNode(selectedCategory.nodeListString)
      } else {
        setDeleteCategoryButtonEnabled(false)
      }
    } else {
      setAddCategoryButtonText('Add Root Category')
      setDeleteCategoryButtonEnabled(false)
      setAddCategoryParentNode('Root')
      setViewCategoryProductsButtonEnabled(false)
    }
  }, [selectedCategory])

  // add category dialog
  const handleAddCategoryClick = () => {
    setAddCategoryDialogOpen(true)
  }

  const handleAddCategorySave = async (newCategory) => {
    const selectedCategoryId = selectedCategory != null ? selectedCategory.id : null

    const addCategoryResponse = await addCategory(selectedSite, newCategory, selectedCategoryId)

    if (addCategoryResponse.code === 200) {
      const newCategoryId = addCategoryResponse.payload.newCategoryId

      const getCategoriesResponse = await getCategories(selectedSite)

      if (getCategoriesResponse.code === 200) {
        setCategoryData(getCategoriesResponse.payload)

        // expand the node if not already so we can see the new node we added (as long as we aren't adding to root node)
        if (categoryTreeRef.current.api.getSelectedNodes().length > 0) {
          categoryTreeRef.current.api.getSelectedNodes()[0].setExpanded(true)
        }

        setTimeout(function () {
          // scroll to the new category, and flash to show it
          categoryTreeRef.current.api.ensureNodeVisible((row) => {
            return (row.data.id === newCategoryId)
          }, 'middle')

          categoryTreeRef.current.api.flashCells({ rowNodes: [categoryTreeRef.current.api.getRowNode(newCategoryId)] })
        }, 250) // if I don't add the timeout, the comparator data doesn't yet seem to include the newly added category

        setAddCategoryDialogOpen(false)
      } else {
        console.log('categories not loaded:', getCategoriesResponse)

        setSnackbarMessage('Unable to load categories')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      }
    } else if (addCategoryResponse.code === 409) {
      setAddCategoryDialogOpen(false)
      setSnackbarMessage(addCategoryResponse.message)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } else {
      console.log('category not added:', addCategoryResponse)

      setAddCategoryDialogOpen(false)
      setSnackbarMessage('Unable to add category')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  const handleAddCategoryClose = () => {
    setAddCategoryDialogOpen(false)
  }

  // delete category dialog
  const handleDeleteCategoryClick = () => {
    setDeleteCategoryDialogOpen(true)
  }

  const handleDeleteCategoryClose = () => {
    setDeleteCategoryDialogOpen(false)
  }

  const handleDeleteCategory = async () => {
    if (selectedCategory == null) {
      return
    }

    const deleteCategoryResponse = await deleteCategory(selectedSite, selectedCategory.id)

    if (deleteCategoryResponse.code === 200) {
      const getCategoriesResponse = await getCategories(selectedSite)

      if (getCategoriesResponse.code === 200) {
        setCategoryData(getCategoriesResponse.payload)

        setDeleteCategoryDialogOpen(false)
      } else {
        console.log('categories not loaded:', getCategoriesResponse)

        setDeleteCategoryDialogOpen(false)
        setSnackbarMessage('Unable to load categories')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      }
    } else {
      console.log('category not deleted:', deleteCategoryResponse)
      setDeleteCategoryDialogOpen(false)

      setSnackbarMessage('Unable to delete category')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  const handleViewCategoryClick = () => {
    history.push(`/app/products/${selectedSite}/category/${selectedCategory.id}`)
  }

  const handleSnackbarClose = (event, reason) => {
    setSnackbarOpen(false)
  }

  return (<>
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="danger" icon>
            <GridContainer>
              <GridItem xs={3} sm={3}>
                <CardIcon color="danger">
                  <CategoryIcon />
                </CardIcon>
              </GridItem>
              <GridItem xs={8} sm={6}>
                <SiteSelector
                  siteList={siteList} showAllSitesOption={false} autoSelect={false}
                  onSelect={onSiteSelected} />
              </GridItem>
              <GridItem xs={1} sm={3}></GridItem>
            </GridContainer>
          </CardHeader>
          <CardBody>
            {
              loading
                ? <LinearProgress />
                : siteLoaded &&
                  <GridContainer>
                    <GridItem xs={12} style={{ marginBottom: '10px' }}>
                      <AtlasButtonBar>
                        <Button variant="contained" color="primary" onClick={handleAddCategoryClick}><AddCircleIcon />&nbsp;{addCategoryButtonText}</Button>
                        <ButtonGroup variant="text">
                          <Button color="primary" disabled={!viewCategoryProductsButtonEnabled} onClick={handleViewCategoryClick}><VisibilityIcon />&nbsp;View Category Products</Button>
                          <Button color="secondary" disabled={!deleteCategoryButtonEnabled} onClick={handleDeleteCategoryClick}><DeleteIcon />&nbsp;Delete Category</Button>
                        </ButtonGroup>
                      </AtlasButtonBar>
                    </GridItem>
                    <GridItem xs={12}>
                      <AtlasTree
                        ref={categoryTreeRef}
                        getRowId={getRowId}
                        getDataPath={getDataPath}
                        rowData={categoryData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        autoGroupColumnDef={autoGroupColumnDef}
                        onSelectionChanged={handleCategorySelected} />
                    </GridItem>
                  </GridContainer>
            }
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
    <AddCategoryDialog onClose={handleAddCategoryClose} onSave={handleAddCategorySave} open={addCategoryDialogOpen} parentCategory={addCategoryParentNode} />
    <DeleteCategoryDialog onClose={handleDeleteCategoryClose} onDeleteCategory={handleDeleteCategory} deleteCategory={deleteCategoryNode} open={deleteCategoryDialogOpen} />
    <Snackbar open={snackbarOpen} autoHideDuration={5000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={handleSnackbarClose}>
      <Alert severity={snackbarSeverity} variant="filled">{snackbarMessage}</Alert>
    </Snackbar>
  </>)
}
